import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Open a file download in a new tab, authenticated with a short-lived token.
 *
 * File URLs have to carry their credential in the query string, because window.open
 * and <a href> cannot send an Authorization header. This used to be the session
 * token — a 7-day, full-account credential that ended up in server access logs,
 * proxy logs, browser history and Referer headers. Now we mint a download-scoped
 * token that expires in 5 minutes, per download.
 *
 * The blank tab is opened synchronously, before any await, so the browser still
 * attributes it to the user's click and does not block it as a popup.
 */
const openDownload = async (url) => {
  const win = window.open('', '_blank');
  try {
    const res = await axios.post(`${API}/auth/download-token`, {}, { headers: getAuthHeader() });
    const sep = url.includes('?') ? '&' : '?';
    const target = `${url}${sep}token=${encodeURIComponent(res.data.token)}`;
    if (win) win.location.href = target;
    else window.location.href = target;   // popup blocked — navigate in place
  } catch (err) {
    if (win) win.close();
    throw err;
  }
};

export const api = {
  openDownload,
  getDownloadToken: () =>
    axios.post(`${API}/auth/download-token`, {}, { headers: getAuthHeader() }),

  // Vessels
  getVessels: () => axios.get(`${API}/vessels`, { headers: getAuthHeader() }),
  getVessel: (id) => axios.get(`${API}/vessels/${id}`, { headers: getAuthHeader() }),
  createVessel: (data) => axios.post(`${API}/vessels`, data, { headers: getAuthHeader() }),
  archiveVessel: (id, reason) => axios.put(`${API}/vessels/${id}/archive`, { reason }, { headers: getAuthHeader() }),
  restoreVessel: (id) => axios.put(`${API}/vessels/${id}/restore`, {}, { headers: getAuthHeader() }),
  updateVesselIMO: (id, imo_number) => axios.put(`${API}/vessels/${id}/imo`, { imo_number }, { headers: getAuthHeader() }),
  updateVesselParticulars: (id, data) => axios.put(`${API}/vessels/${id}/particulars`, data, { headers: getAuthHeader() }),
  
  // Questions
  getQuestions: () => axios.get(`${API}/questions`, { headers: getAuthHeader() }),
  
  // Inspections
  getVesselInspections: (vesselId) => 
    axios.get(`${API}/vessels/${vesselId}/inspections`, { headers: getAuthHeader() }),
  updateInspection: (vesselId, questionId, data) =>
    axios.put(`${API}/vessels/${vesselId}/inspections/${questionId}`, data, { headers: getAuthHeader() }),
  
  // Evidence
  uploadEvidence: (vesselId, questionId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(
      `${API}/vessels/${vesselId}/inspections/${questionId}/evidence`,
      formData,
      { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
    );
  },
  getEvidence: (vesselId, questionId) =>
    axios.get(`${API}/vessels/${vesselId}/inspections/${questionId}/evidence`, { headers: getAuthHeader() }),
  deleteEvidence: (vesselId, questionId, evidenceId) =>
    axios.delete(`${API}/vessels/${vesselId}/inspections/${questionId}/evidence/${evidenceId}`, { headers: getAuthHeader() }),
  getAllVesselEvidence: (vesselId) =>
    axios.get(`${API}/vessels/${vesselId}/evidence/all`, { headers: getAuthHeader() }),
  getEvidenceUrl: (filename) => `${API}/evidence/${filename}`,
  
  // Stats
  getFleetStats: () => axios.get(`${API}/stats/fleet`, { headers: getAuthHeader() }),
  getVesselsStats: (includeArchived = false) => axios.get(`${API}/stats/vessels?include_archived=${includeArchived}`, { headers: getAuthHeader() }),
  getCriticalGaps: () => axios.get(`${API}/stats/critical-gaps`, { headers: getAuthHeader() }),
  getSectionStats: () => axios.get(`${API}/stats/sections`, { headers: getAuthHeader() }),
  getFleetWeakness: () => axios.get(`${API}/stats/fleet-weakness`, { headers: getAuthHeader() }),
  getVesselActivity: () => axios.get(`${API}/stats/vessel-activity`, { headers: getAuthHeader() }),
  exportFleetActionPlan: () => `${API}/export/fleet-action-plan`,
  exportFleetFullAudit:  () => `${API}/export/fleet-full-audit`,
  
  // Public corrective action (no auth)
  getPublicAction: (token) => axios.get(`${API}/public/action/${token}`),
  submitPublicAction: (token, data) => axios.post(`${API}/public/action/${token}/submit`, data),
  uploadPublicEvidence: (token, file, submittedBy) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('submitted_by', submittedBy || '');
    return axios.post(`${API}/public/action/${token}/evidence`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Corrective Actions
  createCorrectiveAction: (vesselId, data) =>
    axios.post(`${API}/vessels/${vesselId}/corrective-actions`, data, { headers: getAuthHeader() }),
  getVesselCorrectiveActions: (vesselId) =>
    axios.get(`${API}/vessels/${vesselId}/corrective-actions`, { headers: getAuthHeader() }),
  updateCorrectiveAction: (actionId, data) =>
    axios.put(`${API}/corrective-actions/${actionId}`, data, { headers: getAuthHeader() }),
  deleteCorrectiveAction: (actionId) =>
    axios.delete(`${API}/corrective-actions/${actionId}`, { headers: getAuthHeader() }),
  getFleetActionPlan: () =>
    axios.get(`${API}/stats/action-plan`, { headers: getAuthHeader() }),

  // Audit Logs
  getAuditLogs: (vesselId) => 
    axios.get(`${API}/vessels/${vesselId}/audit-logs`, { headers: getAuthHeader() }),
  
  // Export
  exportDeficiencyReport: (vesselId) => 
    `${API}/vessels/${vesselId}/export/deficiency`,
  exportFullAuditReport: (vesselId) =>
    `${API}/vessels/${vesselId}/export/full-audit`,
  exportDeficiencyExcel: (vesselId) =>
    `${API}/vessels/${vesselId}/export/deficiency-excel`,
  exportActionPlan: (vesselId) =>
    `${API}/vessels/${vesselId}/export/action-plan`,
  
  // Profile
  changePassword: (current_password, new_password) =>
    axios.put(`${API}/auth/change-password`, { current_password, new_password }, { headers: getAuthHeader() }),

  // Subscription
  getSubscription: () => axios.get(`${API}/subscription`, { headers: getAuthHeader() }),
  
  // Admin
  adminGetUsers: (search = '') => axios.get(`${API}/admin/users?search=${encodeURIComponent(search)}`, { headers: getAuthHeader() }),
  adminGetUser: (userId) => axios.get(`${API}/admin/users/${userId}`, { headers: getAuthHeader() }),
  adminUpdateUser: (userId, data) => axios.put(`${API}/admin/users/${userId}`, data, { headers: getAuthHeader() }),
  adminGetStats: () => axios.get(`${API}/admin/stats`, { headers: getAuthHeader() }),
  adminMigrate: () => axios.post(`${API}/admin/migrate`, {}, { headers: getAuthHeader() }),
  adminBackup: () => axios.post(`${API}/admin/backup`, {}, { headers: getAuthHeader() }),
  getSectionPrep: (vesselId, sectionPrefix) => axios.get(`${API}/vessels/${vesselId}/section-prep/${sectionPrefix}`, { headers: getAuthHeader() }),
  adminGenerateCheckPoints: () => axios.post(`${API}/admin/generate-check-points`, {}, { headers: getAuthHeader() }),
  adminGetCheckPointsReview: (params) => axios.get(`${API}/admin/check-points-review`, { headers: getAuthHeader(), params }),
  adminUpdateCheckPoints: (questionId, data) => axios.patch(`${API}/admin/questions/${questionId}/check-points`, data, { headers: getAuthHeader() }),
  adminDeleteCheckPoints: (questionId) => axios.delete(`${API}/admin/questions/${questionId}/check-points`, { headers: getAuthHeader() }),
  
  // Companies (Admin)
  adminGetCompanies: () => axios.get(`${API}/admin/companies`, { headers: getAuthHeader() }),
  adminCreateCompany: (data) => axios.post(`${API}/admin/companies`, data, { headers: getAuthHeader() }),
  adminUpdateCompany: (companyId, data) => axios.put(`${API}/admin/companies/${companyId}`, data, { headers: getAuthHeader() }),
  adminDeleteCompany: (companyId) => axios.delete(`${API}/admin/companies/${companyId}`, { headers: getAuthHeader() }),
  adminGetCompanyVessels: (companyId) => axios.get(`${API}/admin/companies/${companyId}/vessels`, { headers: getAuthHeader() }),
  adminGetAllVessels: () => axios.get(`${API}/admin/vessels`, { headers: getAuthHeader() }),
  adminMoveVessel: (vesselId, targetCompanyId) => axios.put(`${API}/admin/vessels/${vesselId}/move-company`, { target_company_id: targetCompanyId }, { headers: getAuthHeader() }),
  adminDeleteVessel: (vesselId) => axios.delete(`${API}/admin/vessels/${vesselId}?confirm=${vesselId}`, { headers: getAuthHeader() }),

  // User Admin
  adminDeleteUser: (userId) => axios.delete(`${API}/admin/users/${userId}`, { headers: getAuthHeader() }),
  adminResetPassword: (userId, newPassword) => axios.put(`${API}/admin/users/${userId}/reset-password`, { new_password: newPassword }, { headers: getAuthHeader() }),

  // Invites
  createInvite: (data = {}) => axios.post(`${API}/invites`, data, { headers: getAuthHeader() }),
  getInvites: () => axios.get(`${API}/invites`, { headers: getAuthHeader() }),
  validateInvite: (token) => axios.get(`${API}/invites/${token}/validate`),
  deleteInvite: (inviteId) => axios.delete(`${API}/invites/${inviteId}`, { headers: getAuthHeader() }),

  // Company (Manager)
  getCompanyUsers: () => axios.get(`${API}/company/users`, { headers: getAuthHeader() }),
  getCompanyVessels: () => axios.get(`${API}/company/vessels`, { headers: getAuthHeader() }),
  assignVesselToUser: (userId, vesselId, force = false) => axios.put(`${API}/company/users/${userId}/assign-vessel`, { assigned_vessel_id: vesselId, force }, { headers: getAuthHeader() }),
  assignSuperintendentVessels: (userId, vesselIds) => axios.put(`${API}/company/users/${userId}/assign-superintendent-vessels`, { assigned_vessel_ids: vesselIds }, { headers: getAuthHeader() }),
  updateCompanyUserRole: (userId, role) => axios.put(`${API}/company/users/${userId}/role`, { role }, { headers: getAuthHeader() }),

  // Admin - Pending Managers
  adminGetPendingManagers: () => axios.get(`${API}/admin/pending-managers`, { headers: getAuthHeader() }),
  adminApproveManager: (userId, data) => axios.post(`${API}/admin/approve-manager/${userId}`, data, { headers: getAuthHeader() }),
  adminRejectManager: (userId) => axios.post(`${API}/admin/reject-manager/${userId}`, {}, { headers: getAuthHeader() }),

  // Past Findings Import (Super Admin)
  downloadPastFindingsTemplate: () =>
    axios.get(`${API}/admin/past-findings/template`, { headers: getAuthHeader(), responseType: 'blob' }),
  importPastFindings: (formData) =>
    axios.post(`${API}/admin/past-findings/import`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
    }),

  // Admin - Transfer & Merge
  adminTransferUser: (userId, targetCompanyId) => axios.post(`${API}/admin/transfer-user`, { user_id: userId, target_company_id: targetCompanyId }, { headers: getAuthHeader() }),
  adminMergeCompanies: (sourceId, targetId, vesselLimit) => axios.post(`${API}/admin/merge-companies`, { source_company_id: sourceId, target_company_id: targetId, vessel_limit: vesselLimit }, { headers: getAuthHeader() }),

  // RightShip Official Reports
  uploadRightShipReport: (vesselId, formData) =>
    axios.post(`${API}/vessels/${vesselId}/rightship-reports`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
    }),
  getRightShipReports: (vesselId) =>
    axios.get(`${API}/vessels/${vesselId}/rightship-reports`, { headers: getAuthHeader() }),
  getRightShipReportUrl: (reportId) =>
    `${API}/rightship-reports/${reportId}/file`,
  getFleetCommonFindings: () => axios.get(`${API}/stats/fleet-common-findings`, { headers: getAuthHeader() }),
  getFleetIntelligence: () => axios.get(`${API}/stats/fleet-intelligence`, { headers: getAuthHeader() }),

  // Question Comments
  getQuestionComments: (vesselId, questionId) =>
    axios.get(`${API}/vessels/${vesselId}/inspections/${questionId}/comments`, { headers: getAuthHeader() }),
  postQuestionComment: (vesselId, questionId, text) =>
    axios.post(`${API}/vessels/${vesselId}/inspections/${questionId}/comments`, { text }, { headers: getAuthHeader() }),
  resolveCommentThread: (commentId) =>
    axios.put(`${API}/comments/${commentId}/resolve`, {}, { headers: getAuthHeader() }),
  updateComment: (commentId, text, sendNow = false) =>
    axios.put(`${API}/comments/${commentId}`, { text, send_now: sendNow }, { headers: getAuthHeader() }),
  deleteComment: (commentId) =>
    axios.delete(`${API}/comments/${commentId}`, { headers: getAuthHeader() }),
  getCommentUnreadCounts: (vesselId) =>
    axios.get(`${API}/vessels/${vesselId}/comments/unread-counts`, { headers: getAuthHeader() }),

  // Notifications
  getNotifications: () => axios.get(`${API}/notifications`, { headers: getAuthHeader() }),
  markNotificationsRead: () => axios.put(`${API}/notifications/mark-read`, {}, { headers: getAuthHeader() }),

  // AI Review
  aiReviewVessel: (vesselId, section = null, questionId = null, signal = null) => {
    const params = new URLSearchParams();
    if (section) params.append('section', section);
    if (questionId) params.append('question_id', questionId);
    const qs = params.toString();
    return axios.post(`${API}/vessels/${vesselId}/ai-review${qs ? `?${qs}` : ''}`, {}, { headers: getAuthHeader(), ...(signal ? { signal } : {}) });
  },
  pushAiObservationsToComments: (vesselId, items, sendNow = false) =>
    axios.post(`${API}/vessels/${vesselId}/ai-review/push-to-comments`, { items, send_now: sendNow }, { headers: getAuthHeader() }),
  getDraftCommentsCount: (vesselId) =>
    axios.get(`${API}/vessels/${vesselId}/draft-comments/count`, { headers: getAuthHeader() }),
  getDraftCommentsBySection: (vesselId) =>
    axios.get(`${API}/vessels/${vesselId}/draft-comments/by-section`, { headers: getAuthHeader() }),
  sendDraftComments: (vesselId) =>
    axios.post(`${API}/vessels/${vesselId}/send-draft-comments`, {}, { headers: getAuthHeader() }),

  // Pre-Inspections
  startPreInspection: (vesselId, port = '', visitDate = '') =>
    axios.post(`${API}/vessels/${vesselId}/pre-inspections`, { port, visit_date: visitDate }, { headers: getAuthHeader() }),
  getActivePreInspection: (vesselId) =>
    axios.get(`${API}/vessels/${vesselId}/pre-inspections/active`, { headers: getAuthHeader() }),
  getPreInspections: (vesselId) =>
    axios.get(`${API}/vessels/${vesselId}/pre-inspections`, { headers: getAuthHeader() }),
  reopenPreInspection: (piId) =>
    axios.put(`${API}/pre-inspections/${piId}/reopen`, {}, { headers: getAuthHeader() }),
  completePreInspection: (piId) =>
    axios.put(`${API}/pre-inspections/${piId}/complete`, {}, { headers: getAuthHeader() }),
  savePreInspectionFinding: (piId, questionId, data) =>
    axios.put(`${API}/pre-inspections/${piId}/findings/${questionId}`, data, { headers: getAuthHeader() }),
  getLatestPreInspectionFindings: (vesselId) =>
    axios.get(`${API}/vessels/${vesselId}/pre-inspection-findings/latest`, { headers: getAuthHeader() }),
  submitFindingClosure: (findingId, closure_note) =>
    axios.post(`${API}/pre-inspection-findings/${findingId}/submit-closure`, { closure_note }, { headers: getAuthHeader() }),
  reviewFindingClosure: (findingId, accepted, rejection_reason) =>
    axios.put(`${API}/pre-inspection-findings/${findingId}/review-closure`, { accepted, rejection_reason }, { headers: getAuthHeader() }),
  exportPreInspectionPdf: (piId) =>
    `${API}/pre-inspections/${piId}/export/pdf`,
  getSectionsList: (vesselId) =>
    axios.get(`${API}/vessels/${vesselId}/export/sections-list`, { headers: getAuthHeader() }),
  exportSectionPdf: (vesselId, section) =>
    `${API}/vessels/${vesselId}/export/section-pdf?section=${encodeURIComponent(section)}`,
};
